'use client';

import { resolveDashboardRole } from '@/features/dashboard/lib/backend-role-map';
import type { UserRole } from '@/features/dashboard/lib/widget-registry';
import { useAuthStore } from '@/stores/auth-store';

export interface User {
  id: string;
  name: string;
  /**
   * A BE `Role`-ból FELOLDOTT dashboard-szerep (QA-100).
   * `null` = a szerep nem képezhető le (hiányzó / döntésre váró / ismeretlen) —
   * a fogyasztók ilyenkor a minimális, szerep-független nézetet adják.
   */
  role: UserRole | null;
}

export interface UseAuthReturn {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

/**
 * Auth Hook - bridges useAuthStore to dashboard hooks
 *
 * Maps the real auth store user to the dashboard User interface.
 *
 * QA-100: a szerepet EXPLICIT térképből oldja fel (`resolveDashboardRole`). A
 * korábbi `as UserRole` cast a doksijában azt ígérte, hogy nem-felismert szerepet
 * `'OPERATOR'`-ra ejt — a cast valójában SEMMIT nem ismert fel, így a BE-oldali
 * `BOLTVEZETO` némán átment, és a rá szűrő dashboard-hookok 37-ből 3 widgetet adtak.
 */
export function useAuth(): UseAuthReturn {
  const { user: storeUser, isAuthenticated } = useAuthStore();

  const user: User | null = storeUser
    ? {
        id: storeUser.id,
        name: storeUser.name,
        // M-033 szándéka megtartva és megerősítve: üres/ismeretlen szerep NEM kap
        // implicit OPERATOR-jogot — a resolve ilyenkor `null`-t ad (fail-closed).
        role: resolveDashboardRole(storeUser.role),
      }
    : null;

  return {
    user,
    isAuthenticated,
    isLoading: false,
  };
}
